import { useMutation } from '@graphcommerce/graphql'
import {
  CustomizableProductInterface,
  PROOFING_OPTION,
  ProductInterface,
  SimpleProduct,
} from '@graphcommerce/graphql-mesh'
import {
  ApolloCartErrorAlert,
  useClearCurrentCartId,
  useFormGqlMutationCart,
} from '@graphcommerce/magento-cart'
import { CartItemFragment } from '@graphcommerce/magento-cart-items'
import { OptionTypeRenderer } from '@graphcommerce/magento-product'
import { ProductCustomizableFragment } from '@graphcommerce/magento-product/components/ProductCustomizable/ProductCustomizable.gql'
import { useConfigurableContext } from '@graphcommerce/magento-product-configurable'
import { ConfigurableOptionsInputProps } from '@graphcommerce/magento-product-configurable/ConfigurableOptions/ConfigurableOptions'
import { ConfigurableProductAddToCartMutationVariables } from '@graphcommerce/magento-product-configurable/graphql/ConfigurableProductAddToCart.gql'
import { Money } from '@graphcommerce/magento-store'
import { extendableComponent } from '@graphcommerce/next-ui'
import { i18n } from '@lingui/core'
import { Alert, Box, SxProps, Theme, SwipeableDrawer, MenuItem } from '@mui/material'
import React, { useCallback, useEffect, useState, useContext, useMemo } from 'react'
import { AttachArtworkDocument } from '../Artwork/AttachArtwork.gql'
import { HighlightButton } from '../Button/ButtonStyles'
import { StandardDropdown } from '../Dropdown/StandardDropdown'
import { fbqAddToCart } from '../FacebookPixel/events/fbqAddToCart'
import { FileUploadModal, ProofOptionEnum, s3File } from '../FileUpload/FileUploadModal'
import { gtagAddToCart } from '../GoogleAnalytics/events/gtagAddToCart'
import { klaviyoAddToCart } from '../Klaviyo/events/klaviyoAddToCart'
import { globalContext } from '../NextUi/globalContext'
import { AttachProjectDataDocument } from '../PitchPrint/AttachProjectData.gql'
import { SimpleProductAddToCartDocument } from '../ProductSimple/SimpleProductAddToCart.gql'
import { ErrorPrompt } from '../Prompt/ErrorPrompt'
import { ArtworkRequirements } from './Artwork/ArtworkRequirements'
import { AddedToCart } from './Minicart/AddedToCart'
import { ProductCustomizable } from './ProductCustomizable/ProductCustomizable'

type ConfigurableProductAddToCartProps = {
  variables: Omit<ConfigurableProductAddToCartMutationVariables, 'cartId' | 'selectedOptions'>
  product: Pick<
    ProductInterface,
    | 'name'
    | 'sku'
    | 'requires_artwork'
    | 'categories'
    | 'uid'
    | 'design_id'
    | 'setup_price'
    | 'setup_price_discount'
    | 'setup_price_quantity_threshold'
    | 'setup_price_type'
    | 'price_range'
  >
  name: string
  optionEndLabels?: Record<string, React.ReactNode>
  children?: React.ReactNode
  additionalButtons?: React.ReactNode
  sx?: SxProps<Theme>
  optionsProps?: Omit<
    ConfigurableOptionsInputProps,
    'name' | 'sku' | 'control' | 'rules' | 'errors' | 'optionEndLabels'
  >
  callback?: () => void
}

const compName = 'ConfigurableOptionsInput' as const
const parts = ['form', 'button', 'finalPrice', 'quantity', 'divider', 'buttonWrapper'] as const
const { classes } = extendableComponent(compName, parts)

export function FourOverProductAddToCart(props: ConfigurableProductAddToCartProps) {
  const {
    name,
    children,
    variables,
    optionEndLabels,
    optionsProps,
    additionalButtons,
    product,
    sx = [],
    callback,
    ...buttonProps
  } = props

  const [attachArtwork] = useMutation(AttachArtworkDocument)
  const [attachProjectData] = useMutation(AttachProjectDataDocument)
  const [artworkFiles, setArtworkFiles] = useState<
    Array<
      s3File & {
        type?: string | undefined
      }
    >
  >([])
  const [ppProjectId, setPpProjectId] = useState<null | string>(null)
  const [isDisplayFileUploadModal, setIsDisplayFileUploadModal] = useState<boolean>(false)
  const { drawer, setDrawer } = useContext(globalContext)
  const [addedToCartDrawer, setAddedToCartDrawer] = useState<boolean | null>(null)
  const [shouldRetry, setShouldRetry] = useState(false)

  const [isDisplayErrorPrompt, setIsDisplayErrorPrompt] = useState<boolean>(false)
  const [attachError, setAttachError] = useState<Error>()
  const clearCurrentCartId = useClearCurrentCartId()
  const { getVariants, selection } = useConfigurableContext(variables.sku)
  const selectedVariant = getVariants(selection)
  const selectedVariantDesignId = selectedVariant?.at(0)?.product?.design_id
  const requiresArtwork = !selectedVariantDesignId && product.requires_artwork
  const [setupPriceDisplay, setSetupPriceDisplay] = useState('0')
  const [totalPriceDisplay, setTotalPriceDisplay] = useState('0')
  const [artworkComment, setArtworkComment] = useState('')
  const [proofOption, setProofOption] = useState<ProofOptionEnum>(ProofOptionEnum.wait_for_proof)

  const handleUploadArtwork = () => {
    setIsDisplayFileUploadModal(true)
  }

  const form = useFormGqlMutationCart(SimpleProductAddToCartDocument, {
    defaultValues: { ...variables },
    onBeforeSubmit: ({ ...vars }) => {
      if ((requiresArtwork && !artworkFiles.length) || (selectedVariantDesignId && !ppProjectId)) {
        return false
      }

      // We replace option value uids with ids, coz backend does not work with uids. We could just work with uids in
      // frontend, but 1. some things are already done with uis by default 2. we think backend will change those to uids in the near future
      const enteredOptions = vars.enteredOptions?.map((option) => {
        if (
          option &&
          option.value &&
          option?.value.length > 8 &&
          'options' in product &&
          Array.isArray(product.options)
        ) {
          // This means uid is used instead of id
          const optionData = product.options.find((op) => op.uid === option.uid)
          if ('dropdownValue' in optionData && Array.isArray(optionData.dropdownValue)) {
            const valueData = optionData.dropdownValue.find((v) => v.uid === option.value)
            if (valueData) {
              return {
                ...option,
                value: valueData.option_type_id.toString(),
              }
            }
          }
        }
        return option
      })

      return {
        ...vars,
        enteredOptions,
        quantity: 1,
      }
    },
    onComplete: ({ data, errors }, vars) => {
      // When we have an invalid cartId (maybe they have been previously signIn as a customer, now the customer token is expired, but cartId is still there)
      if (
        errors?.find((error) => error.message.indexOf('user cannot perform operations on cart') > 0)
      ) {
        clearCurrentCartId()
        setShouldRetry(true)
        return
      }

      if (data?.addProductsToCart?.user_errors.length) {
        return
      }

      const addedItem = data?.addProductsToCart?.cart.items?.slice(-1)[0]

      gtagAddToCart(addedItem as unknown as CartItemFragment, 1)

      const category = product.categories?.length
        ? product.categories?.at(product.categories.length - 1)?.name
        : ''

      fbqAddToCart(addedItem as unknown as CartItemFragment, 1, category || '')
      klaviyoAddToCart(addedItem as unknown as CartItemFragment, 1, category || '')

      const cartItemUid = data?.addProductsToCart?.cart.items?.slice(-1)[0]?.uid ?? ''

      if (data && artworkFiles && artworkFiles.length > 0) {
        artworkFiles.forEach(async (artworkFile) => {
          await attachArtwork({
            variables: {
              cartItemUid,
              fileName: artworkFile.fileName || '',
              fileUrl: artworkFile.url || '',
              extra: JSON.stringify({ type: artworkFile.type || '' }),
              proofingOption:
                (proofOption.toString() as PROOFING_OPTION) ?? ('' as PROOFING_OPTION),
              comments: artworkComment ?? '',
            },
          })
            .catch((error: Error) => {
              setAttachError(error)
              setIsDisplayErrorPrompt(true)
            })
            .finally(() => setArtworkFiles([]))
        })
      }
      if (data && ppProjectId) {
        attachProjectData({
          variables: {
            cartItemUid,
            cartUid: data.addProductsToCart?.cart.id as string,
            projectId: ppProjectId,
          },
        })
          .then(() => {
            setPpProjectId(null)
          })
          .catch((error: Error) => {})
      }
    },
  })

  const { handleSubmit, formState, required, control, error, data, setValue, watch } = form
  const submitHandler = handleSubmit(() => {})

  useEffect(() => {
    if (shouldRetry) {
      setShouldRetry(false)
      submitHandler()
        .then(() => {})
        .catch(() => {})
    }
  }, [shouldRetry, submitHandler])

  useEffect(() => {
    if (formState.isSubmitting) {
      setAddedToCartDrawer(null)
      setDrawer({ id: null })
    }
  }, [formState.isSubmitting, setDrawer, setAddedToCartDrawer])

  useEffect(() => {
    if (
      !formState.isSubmitting &&
      formState.isSubmitSuccessful &&
      !error?.message &&
      !data?.addProductsToCart?.user_errors?.length &&
      drawer.id === null &&
      addedToCartDrawer === null &&
      (!product.requires_artwork || artworkFiles.length || ppProjectId)
    ) {
      setDrawer({ id: 'added_to_cart' })
      setAddedToCartDrawer(true)
    }
  }, [
    data,
    error,
    formState,
    drawer,
    setDrawer,
    addedToCartDrawer,
    setAddedToCartDrawer,
    product,
    artworkFiles,
    ppProjectId,
  ])

  // const action = useAddProductsToCartAction(props)
  // For 4Over, we store runsizes (quantities) with colorSpecs, because they 2 together define the baseprice
  // But we want to show them in nice way, in seperate dropdowns anyways
  const optionsAdjusted = useMemo(() => {
    const quantites: Record<number, number> = {}
    if ('options' in product && Array.isArray(product.options)) {
      const options = product.options.map((option) => {
        if (option.title === 'Colorspec') {
          let values = []
          if (option.dropdownValue) {
            values = option.dropdownValue
              .filter((v) => v.price)
              .map((v) => {
                const valueParts = v.title.split(' - ')
                if (valueParts.length > 1) {
                  quantites[parseInt(valueParts[1] as string, 10)] = v.price
                  return {
                    ...v,
                    title: valueParts[0],
                  }
                }
                return v
              })
          }
          return {
            ...option,
            dropdownValue: values,
          }
        }
        return option
      })
      return {
        options,
        quantities: Object.fromEntries(
          Object.entries(quantites).sort((a, b) => a[0].localeCompare(b[0])),
        ),
      }
    }
    return {
      options: [],
      quantities: {},
    }
  }, [product])

  const enteredOptions = watch('enteredOptions')

  const getProductPlusSetupPrice = useCallback(
    (quantity: number, rowPrice: number) => {
      const selectedProduct = product
      let setupPrice = selectedProduct?.setup_price ?? 0
      if (selectedProduct) {
        const { setup_price_discount, setup_price_quantity_threshold } = selectedProduct
        if (setup_price_quantity_threshold && quantity > setup_price_quantity_threshold) {
          setupPrice = 0
        } else if (setup_price_discount) {
          return { rowPrice, setupPrice: setup_price_discount }
        }
      }

      return { rowPrice, setupPrice }
    },
    [product],
  )

  const quantityDropdownOptions = useMemo(
    () =>
      (optionsAdjusted &&
        Object.keys(optionsAdjusted.quantities).map((qty) => {
          const totalPrice = optionsAdjusted.quantities[qty]
          const price = parseFloat((totalPrice / parseInt(qty, 10)).toFixed(4))

          return {
            value: qty || '',
            labels: [qty?.toLocaleString() || '', price || 0],
            price,
            totalPrice,
            priceWithSetup: getProductPlusSetupPrice(parseInt(qty, 2), totalPrice),
          }
        })) ||
      [],
    [getProductPlusSetupPrice, optionsAdjusted],
  )

  const colorSpecOption = (product as CustomizableProductInterface).options?.find(
    (p) => p?.title === 'Colorspec',
  )
  const colorSpecOptionValue =
    enteredOptions && colorSpecOption
      ? (
          colorSpecOption as React.ComponentProps<OptionTypeRenderer['CustomizableDropDownOption']>
        )?.dropdownValue?.find(
          (v) =>
            v?.uid ===
            (enteredOptions || []).find((eo) => eo && eo.uid === colorSpecOption.uid)?.value,
        )
      : null

  // Autoselect the first quantity if no quantity if specified
  const quantity = watch('quantity')
  useEffect(() => {
    if (!quantity && colorSpecOptionValue && optionsAdjusted.quantities) {
      const firstQuantity = Object.keys(optionsAdjusted.quantities).at(0)
      if (firstQuantity) {
        setValue('quantity', Number.parseInt(firstQuantity, 10))
      }
    }
  }, [quantity, optionsAdjusted, setValue, colorSpecOptionValue])

  // Update "Set up" and "Total" sections
  useEffect(() => {
    const qtyDetails = quantityDropdownOptions.find((q) => q.value === quantity?.toString())
    if (qtyDetails) {
      setSetupPriceDisplay(qtyDetails?.priceWithSetup?.setupPrice?.toString() ?? '0')
      setTotalPriceDisplay(
        (
          (qtyDetails?.priceWithSetup?.rowPrice ?? 0) +
          (qtyDetails?.priceWithSetup?.setupPrice ?? 0)
        ).toString() ?? '0',
      )
    }
  }, [quantity, quantityDropdownOptions])

  return (
    <>
      <Box
        component='form'
        onSubmit={submitHandler}
        noValidate
        className={classes.form}
        sx={[
          {
            width: '100%',
          },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
      >
        <div className='mb-[15px] mt-[10px] h-[1px] bg-20-grey' />

        <ProductCustomizable
          product={{ ...product } as SimpleProduct & ProductCustomizableFragment}
          form={form}
          selectedVariantSku=''
          quantities={optionsAdjusted.quantities}
          colorSpecOptionValue={colorSpecOptionValue}
        />

        {'quantities' in optionsAdjusted && colorSpecOptionValue && (
          <div className='my-[15px]'>
            <StandardDropdown
              value={watch('quantity') || ''}
              label='Quantity'
              onChange={(selectedOption) => {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                setValue('quantity', selectedOption?.target?.value)
              }}
              options={quantityDropdownOptions.map((option) => (
                <MenuItem
                  value={option.value}
                  className='flex justify-between pr-5'
                  sx={{ justifyContent: 'space-between' }}
                >
                  {option.labels?.map((item, index) =>
                    index === 0 ? <span>{item}</span> : <span>{`$${item} each`}</span>,
                  )}
                </MenuItem>
              ))}
              twoColumns
              fullWidth
            />
          </div>
        )}
        {!!colorSpecOptionValue && (
          <div className='mt-[15px] flex flex-col gap-3 bg-[#000000] bg-opacity-[0.03] px-5 py-3'>
            {setupPriceDisplay !== '0' && (
              <div className='Type-Large-Regular flex items-center justify-between'>
                <span>Set up</span>
                <span>
                  <Money
                    value={parseFloat(setupPriceDisplay)}
                    currency={product?.price_range?.minimum_price?.regular_price?.currency}
                  />
                </span>
              </div>
            )}
            <div className='Type-XL-Medium flex items-center justify-between'>
              <span>Total</span>
              <span>
                <Money
                  value={parseFloat(totalPriceDisplay)}
                  currency={product?.price_range?.minimum_price?.regular_price?.currency}
                />
              </span>
            </div>
          </div>
        )}

        <div className='mt-[15px] h-[1px] bg-20-grey' />
        {children}
        <div className='mb-5'>
          {artworkFiles.length > 0 &&
            artworkFiles?.map((item, index) => (
              <div
                key={item.fileName}
                className='lg:Type-Large-Medium Type-Regular-Medium inline border border-0 border-r-2 border-black px-2 text-templi-purple underline last:border-r-0'
              >
                <a href={item.url} target='_blank' rel='noreferrer'>
                  {item.fileName}
                </a>
              </div>
            ))}
        </div>

        {colorSpecOptionValue && (
          <ArtworkRequirements
            productUuid={product.uid}
            enteredOptions={
              Array.isArray(enteredOptions) && enteredOptions.length
                ? enteredOptions
                    .filter((o) => o)
                    .map((o) => (o && o.value ? o?.value.toString() : ''))
                : []
            }
            artworkFiles={artworkFiles}
            setArtworkFiles={setArtworkFiles}
            proofOption={proofOption}
            setProofOption={setProofOption}
            artworkComment={artworkComment}
            setArtworkComment={setArtworkComment}
            callback={callback}
          />
        )}

        <div>
          <HighlightButton
            type='submit'
            variant='contained'
            // loading={formState.isSubmitting}
            className='add-to-cart w-full bg-marigold uppercase'
            disabled={
              (requiresArtwork && !artworkFiles.length) ||
              (!!selectedVariantDesignId && !ppProjectId)
            }
            loading={formState.isSubmitting}
            {...buttonProps}
          >
            {i18n._(/* i18n */ 'Add to Cart')}
          </HighlightButton>
          {additionalButtons}
        </div>
        {/* <AddProductsToCartButton fullWidth product={product} /> */}

        <ApolloCartErrorAlert error={error} />

        {data?.addProductsToCart?.user_errors.map((e) => (
          <Box key={e?.code}>
            <Alert severity='error'>{e?.message}</Alert>
          </Box>
        ))}

        <FileUploadModal
          modalId='fileUploadModal'
          isDiplayed={isDisplayFileUploadModal}
          setIsDisplayed={setIsDisplayFileUploadModal}
          setUploadedFile={setArtworkFiles}
          files={artworkFiles}
          artworkComment={artworkComment}
          setArtworkComment={setArtworkComment}
          proofOption={proofOption}
          setProofOption={setProofOption}
          callback={callback}
        />
        <ErrorPrompt error={attachError ?? error} />
      </Box>
      <SwipeableDrawer
        anchor='right'
        onClose={() => {
          setDrawer({ id: false })
          setAddedToCartDrawer(false)
        }}
        onOpen={() => {}}
        open={drawer.id === 'added_to_cart'}
        classes={{
          paper: 'rounded-l-[10px] max-w-[520px]',
        }}
      >
        <AddedToCart
          sku={variables.sku}
          onClose={() => {
            setDrawer({ id: false })
            setAddedToCartDrawer(false)
          }}
        />
      </SwipeableDrawer>
    </>
  )
}
